import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'

// Single source of truth for how a screen's status is DISPLAYED across every
// surface — dashboard, screens page, screen details, screen groups, and the
// admin. Mirrors the backend's app/services/screen_state.py:effective_status
// so a license-expired or unpaired screen never reads "online" anywhere.
// Every surface must call THIS function (and render via ScreenStatusBadge /
// ScreenStatusDot) rather than deriving its own status locally — a page that
// reimplements the license-expiry math inevitably drifts from this one and
// shows a different status for the same screen (the "expiring soon" pill
// that only ever showed on the Dashboard, nowhere else, was exactly that bug).
export type ScreenStatusLike = {
  status?: string
  effective_status?: string
  disconnect_at?: string | null
  is_paired?: boolean
  /** Operator's on/off switch. A deactivated screen keeps heartbeating (that
   *  is how it notices being switched back on), so without this it reads
   *  "online" in the dashboard while the wall shows the deactivated notice. */
  is_active?: boolean
  /** Whether any hardware is bound. False after Detach (or the 72h binding
   *  sweep): the screen is waiting to be given a device, which is a different
   *  thing from a panel that is switched off. */
  has_device?: boolean
}

// A license within this window of expiring shows "expiring" instead of
// "online" — surfaced early enough for an operator to renew before it lapses.
export const LICENSE_EXPIRING_WINDOW_MS = 48 * 3600 * 1000

/** Resolve the one status label to show. License urgency (expired / expiring
 *  soon) wins over plain connectivity — a screen that's about to lose its
 *  license is worth flagging even while it's still online. Otherwise prefers
 *  the backend's effective_status, falling back to a local derivation so an
 *  older payload can't leak "online" for an expired/unpaired screen.
 *
 *  "license_fallback" (Enterprise-only, see app/services/license_fallback.py
 *  on the backend) is checked FIRST, ahead of the raw disconnect_at check
 *  below — that check can't tell "gracefully showing the fallback image"
 *  apart from "dead, needs re-pairing" (both have a past disconnect_at), so
 *  without this the local fallback would always win and show "expired"
 *  even when the backend explicitly said otherwise. */
export function screenDisplayStatus(s: ScreenStatusLike): string {
  if (s.effective_status === 'license_fallback') return 'license_fallback'
  const expMs = s.disconnect_at ? new Date(s.disconnect_at).getTime() : null
  if (expMs !== null && expMs <= Date.now()) return 'expired'
  // Deactivated ranks under the license states (those need billing action)
  // but over everything else, INCLUDING effective_status. Deliberately local
  // rather than trusting the server's field: the screens list flips this
  // switch optimistically, patching only `is_active` on the row it already
  // holds, so reading effective_status first would leave the badge saying
  // "online" until the next refetch — the operator toggles Active off and
  // watches nothing happen.
  if (s.is_active === false) return 'deactivated'
  if (expMs !== null && expMs <= Date.now() + LICENSE_EXPIRING_WINDOW_MS) return 'expiring'
  if (s.effective_status) return s.effective_status
  // Local fallback for payloads without effective_status. Ranked above the
  // is_paired check because a detached screen is ALSO unpaired, and "offline"
  // is the less useful of the two answers.
  if (s.has_device === false) return 'detached'
  if (s.is_paired === false) return 'offline'
  return s.status || 'offline'
}

// Solid backgrounds, not soft tints — an alert state should read as an
// unmistakable warning at a glance (same rule as ManagementColumn's Danger
// Zone and the ExpiredScreenBanner), not blend into the row as a faint tint.
// license_fallback gets its own calm blue, distinct from amber (expiring,
// "act soon") and red (expired, "broken") — it's neither: the license did
// lapse, but the screen is handling it gracefully with no action required.
const TONE: Record<string, string> = {
  online:   'bg-muted text-primary',
  expiring: 'bg-amber-500 text-white',
  expired:  'bg-destructive text-destructive-foreground',
  license_fallback: 'bg-blue-500 text-white',
  // Deliberately NOT an alert tone: deactivation is a switch someone chose to
  // flip, not a fault. It reads as a distinct, deliberate "off" rather than a
  // red problem or a grey "we can't reach it".
  deactivated: 'bg-slate-500 text-white',
  // Same reasoning — an awaited action, not a failure. Amber-adjacent would
  // imply something is wrong; this is a screen ready to be given hardware.
  detached: 'bg-indigo-500 text-white',
}

/** The shared status chip. Same shape/tone rules everywhere.
 *  Sized up from the Badge primitive's default 10px/font-bold — at that size
 *  + uppercase + wide tracking, "EXPIRING"/"OFFLINE" read as a blurry smear
 *  on a lot of displays. semibold (not bold) + a touch more size/padding
 *  keeps the same "unmistakable at a glance" pill but actually legible. */
export function ScreenStatusBadge({
  screen, className, title,
}: { screen: ScreenStatusLike; className?: string; title?: string }) {
  const { t } = useTranslation()
  const ds = screenDisplayStatus(screen)
  const tone = TONE[ds] ?? 'bg-muted text-muted-foreground'
  const label = t(`common.${ds}`, ds)
  return (
    <Badge
      variant="outline"
      title={title}
      className={cn(
        'text-[11px] font-semibold uppercase tracking-wide py-0 px-2.5 h-[20px] border-0',
        tone, className,
      )}
    >
      {label}
    </Badge>
  )
}

/** Just the status dot (for compact rows / detail headers). */
export function ScreenStatusDot({ screen, className }: { screen: ScreenStatusLike; className?: string }) {
  const ds = screenDisplayStatus(screen)
  const tone = ds === 'online' ? 'bg-primary'
    : ds === 'expiring' ? 'bg-amber-500'
    : ds === 'expired'  ? 'bg-destructive'
    : ds === 'license_fallback' ? 'bg-blue-500'
    : ds === 'deactivated' ? 'bg-slate-500'
    : ds === 'detached' ? 'bg-indigo-500'
    : 'bg-muted-foreground/50'
  return <span className={cn('inline-block w-2.5 h-2.5 rounded-full', tone, className)} />
}
